import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MoveRight } from "lucide-react";
import TunnelScene from "./TunnelScene";
import { scrollToId } from "../lib/lenis";
import { beep } from "../lib/sound";

const reveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.35 + i * 0.16 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  const logoOpacity = useTransform(scrollYProgress, [0, 0.18], [1, 0]);
  const logoY = useTransform(scrollYProgress, [0, 0.18], [0, -90]);
  const logoScale = useTransform(scrollYProgress, [0, 0.18], [1, 1.12]);
  const uiOpacity = useTransform(scrollYProgress, [0, 0.26], [1, 0]);

  return (
    <section
      id="entrance"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[380vh] bg-void"
    >
      <div className="sticky top-0 h-screen overflow-hidden">
        <TunnelScene progress={scrollYProgress} />

        {/* CRT texture over the tunnel feed */}
        <div className="scanlines-layer pointer-events-none absolute inset-0 z-10 opacity-35" />
        <div className="crt-vignette pointer-events-none absolute inset-0 z-10" />

        {/* center logo lockup — fades as you descend */}
        <motion.div
          style={{ opacity: logoOpacity, y: logoY, scale: logoScale }}
          className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center px-6 text-center"
        >
          <h1
            className="title-tall overflow-hidden"
            style={{ fontSize: "min(30vw, 45vh)", lineHeight: 0.92 }}
          >
            <motion.span
              custom={0}
              variants={reveal}
              initial="hidden"
              animate="show"
              data-testid="hero-logo"
              className="crt-text block font-poster"
            >
              GUMZ
            </motion.span>
          </h1>
          <span className="relative z-10 block overflow-hidden" style={{ marginTop: "min(4vw, 6vh)" }}>
            <motion.span
              custom={1}
              variants={reveal}
              initial="hidden"
              animate="show"
              data-testid="hero-sublogo"
              className="block pl-[0.9em] font-mono text-sm font-bold tracking-[0.9em] text-[#dcfbf3] sm:text-lg"
            >
              STUDIOS
            </motion.span>
          </span>
        </motion.div>

        {/* CTA */}
        <motion.div
          style={{ opacity: uiOpacity }}
          className="absolute inset-x-0 bottom-8 z-20 flex flex-col items-center gap-3"
        >
          <motion.button
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            data-testid="access-terminal-button"
            onClick={() => {
              beep(980, 0.1, 0.05);
              scrollToId("#platform");
            }}
            className="btn-y2k group flex items-center gap-3 px-9 py-4 font-mono text-sm font-bold tracking-[0.25em]"
          >
            ACCESS SHOP TERMINAL
            <MoveRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-2"
            />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
}
